'use client';

import { trackLinkClick } from 'utils/tracking';

import ButtonLink, { ButtonLinkType } from '@/components/ui/ButtonLink';
import CDNImage from '@/components/ui/CDNImage';
import CloseIcon from '@/components/ui/CloseIcon';
import Modal from '@/components/ui/Modal';
import PlayVideoIcon from '@/components/ui/PlayVideoIcon';
import VideoClip from '@/components/ui/VideoClip';
import { Page_Pagebuilder_Blocks_ContentList } from '@/graphql/generated/graphql';
import { m } from 'motion/react';
import { useState } from 'react';
import { containerVariants, itemVariants, viewPort } from 'utils/framer';
import { getContainerClass } from 'utils/helpers';
import Wysiwyg from '../components/dom-parser';

type LayoutType = 'columns' | 'rows' | 'alternating';

const ContentBlocks = ({
  blockId,
  containerWidth,
  layout = 'columns',
  firstItemReversed,
  list,
  smallGap,
}: Page_Pagebuilder_Blocks_ContentList) => {
  const [currentVideoPlaying, setCurrentVideoPlaying] = useState(null);
  const isLayout3Cols = layout === 'columns';
  const isLayout1Row2Cols = layout === '1row2cols';
  const isLayout2Cols = layout === '2columns';
  const isLayoutRows = layout === 'rows';
  const isLayoutAlternating = layout === 'alternating';

  if (!list || (list && list.length === 0)) return null;
  return (
    <section id={blockId ?? undefined} className="w-full my-14 lg:my-24">
      <div className={`w-full ${getContainerClass(containerWidth)}`}>
        <m.ul
          initial="hidden"
          whileInView="visible"
          viewport={viewPort}
          variants={containerVariants}
          className={`flex ${isLayoutRows ? 'flex-col gap-8 lg:gap-6' : isLayoutAlternating ? `flex-col gap-8${smallGap ? '' : ' lg:gap-24'}` : isLayout2Cols ? 'flex-wrap gap-8 lg:gap-10' : isLayout1Row2Cols ? 'grid grid-cols-1 lg:grid-cols-2 gap-y-14 lg:gap-y-12 gap-x-12' : 'flex-col lg:flex-row gap-8 lg:gap-6'}`}
        >
          {list.map((item, i) => {
            const contentWithBg = item.contentBg ?? false;
            const isOdd = firstItemReversed
              ? (i + 1) % 2 === 0
              : (i + 1) % 2 != 0;
            return (
              <m.li
                initial="hidden"
                whileInView="visible"
                viewport={viewPort}
                variants={itemVariants}
                key={i}
                className={`${isLayoutRows && contentWithBg ? 'grid grid-cols-1 lg:grid-cols-[30%,1fr]' : isLayoutRows ? 'grid grid-cols-1 lg:grid-cols-[340px,1fr] gap-4 lg:gap-12' : isLayoutAlternating ? 'grid grid-cols-1 lg:grid-cols-2 lg:items-center gap-4 lg:gap-0' : isLayout2Cols ? 'lg:basis-[calc(50%-20px)] flex flex-col gap-4 lg:gap-6' : isLayout1Row2Cols ? 'flex-1 flex flex-col gap-4 lg:gap-6 lg:first:col-span-2 first:grid first:grid-cols-1 first:lg:grid-cols-2 first:gap-x-12' : 'flex-1 flex flex-col gap-4 lg:gap-6'}`}
              >
                {item.video || item.image ? (
                  <div
                    className={`w-full relative ${isLayoutAlternating && item.mediaIsCover ? 'h-full' : ''} ${isLayoutAlternating ? 'aspect-[3/2]' : isLayout3Cols ? 'aspect-[4/3]' : isLayoutRows ? '' : 'aspect-video'} ${isLayoutRows && contentWithBg ? 'w-full h-full' : isLayoutRows ? 'lg:w-[340px]' : ''} ${isLayoutAlternating ? (isOdd ? 'order-1' : 'order-1 lg:order-2') : ''}`}
                    onClick={() => setCurrentVideoPlaying(item.video)}
                  >
                    {item.video ? (
                      <>
                        <PlayVideoIcon className="w-16 h-16 text-white absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                        {item.image ? (
                          <CDNImage
                            src={item.image.sourceUrl || ''}
                            alt={item.image.altText || ''}
                            width={item.image.mediaDetails.width || 0}
                            height={item.image.mediaDetails.height || 0}
                            sizes="(max-width: 1024px) 100vw, 570px"
                            srcSet={item.image.srcSet || ''}
                            className="w-full h-full object-cover aspect-video"
                          />
                        ) : (
                          <video
                            className="w-full h-full object-cover aspect-video cursor-pointer"
                            muted
                            playsInline
                            preload="metadata"
                          >
                            <source
                              src={`${item.video.mediaItemUrl}#t=0.001`}
                              type={item.video.mimeType}
                            />
                          </video>
                        )}
                      </>
                    ) : item.image && item.image.sourceUrl ? (
                      <CDNImage
                        src={item.image.sourceUrl || ''}
                        alt={item.image.altText || ''}
                        width={item.image.mediaDetails.width || 0}
                        height={item.image.mediaDetails.height || 0}
                        sizes="(max-width: 1024px) 100vw, 570px"
                        srcSet={item.image.srcSet || ''}
                        className={`w-full ${isLayoutAlternating ? (item.mediaIsCover ? 'object-cover h-full aspect-video' : 'h-auto object-contain') : isLayoutRows ? 'h-auto object-contain' : 'object-cover h-full aspect-video'}`}
                      />
                    ) : null}
                  </div>
                ) : null}

                {item.content && (
                  <div
                    className={`flex flex-col h-full px-4 md:px-0 ${isLayoutAlternating ? 'lg:px-12 lg:justify-center' : ''} ${isLayoutAlternating ? (isOdd ? 'order-2' : 'order-2 lg:order-1') : ''} ${contentWithBg ? 'p-4 lg:pl-8 lg:pr-4 lg:pt-6 lg:pb-4' : ''} editor-card ${isLayout1Row2Cols ? 'editor-card-lg' : ''}`}
                    style={{
                      backgroundColor: item.contentBg
                        ? item.contentBg
                        : 'transparent',
                    }}
                  >
                    <Wysiwyg>{item.content}</Wysiwyg>
                    {item.link && item.linkText && (
                      <div
                        className={`${isLayout2Cols || isLayout3Cols ? 'mt-auto' : ''} ${item.linkPosition === 'center' ? 'mx-auto' : item.linkPosition === 'right' ? 'max-lg:mx-auto lg:ml-auto' : 'max-lg:mx-auto lg:mr-auto'}`}
                      >
                        <ButtonLink
                          onClick={trackLinkClick}
                          type={item.linkType as ButtonLinkType}
                          text={item.linkText}
                          url={item.link}
                          linkInNewWindow={item.linkInNewWindow}
                          className={
                            isLayout3Cols ? 'mt-2 lg:mt-4' : 'mt-2 lg:mt-8'
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
              </m.li>
            );
          })}
        </m.ul>
      </div>
      {currentVideoPlaying ? (
        <Modal>
          <div className="flex justify-center items-center w-full h-full">
            <button
              aria-label="Close window"
              className="absolute top-6 right-6 flex items-center justify-center w-10 h-10 hover:bg-black/10 opacity-60 hover:opacity-100 rounded-full transition-all z-10"
              onClick={() => setCurrentVideoPlaying(null)}
            >
              <CloseIcon className="w-6 h-6 text-white" />
            </button>
            <VideoClip controls autoplay {...currentVideoPlaying} />
          </div>
        </Modal>
      ) : null}
    </section>
  );
};

export default ContentBlocks;
